<script setup lang="ts">
import { computed, ref } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { PlanEditTarget, useBasePriceStore } from "@/modules/base_price/store";
import { CalendarWeek, DayMap, dayBudgetsFor } from "@/modules/base_price/utils/dayDistribution";
import {
	DayPrice,
	isOffBudget,
	priceDay,
	solveFillForCpm,
	sumDays,
} from "@/modules/base_price/utils/dayPricing";
import { useMonthCalendar } from "@/modules/base_price/composables/useMonthCalendar";
import { useMonthDayRows } from "@/modules/base_price/composables/useMonthDayRows";
import PlanMonthCalendarGrid from "@/modules/base_price/components/PlanMonthCalendarGrid.vue";
import PlanRevenueGauge from "@/modules/base_price/components/PlanRevenueGauge.vue";
import { CPM_COLOR, CPM_TINT, FILL_COLOR, FILL_TINT } from "@/modules/base_price/utils/metricColors";

/**
 * One month of the Fill & CPM tab, by day: how each day earns the budget the
 * Monthly Budget Distribution calendar gave it. A day follows its month's fill
 * and price until it gets its own, exactly as a month follows its parent until
 * it gets its own — so this is the month chart one level down, sharing its
 * Hold budget switch and its Fill rate | CPM toggle with the tab.
 *
 * The frame, the impressions and the per-month rows are shared with the
 * Monthly Budget Distribution calendar (useMonthCalendar, useMonthDayRows,
 * PlanMonthCalendarGrid); the arithmetic is utils/dayPricing.ts, mirroring
 * the resolver's MonthDayPricer.
 *
 * A true draft: nothing reaches the resolver until Apply.
 */
const props = defineProps<{
	target: PlanEditTarget;
	/** The tab's Hold budget switch — one for the whole configuration, months and days alike. */
	hold: boolean;
	/** The tab's Fill rate | CPM toggle: which figure leads in every cell. */
	metric: "fill" | "cpm";
}>();

/** 1-12 — shared with the tab, so the arrows and the breadcrumb agree. */
const month = defineModel<number>("month", { required: true });

const emit = defineEmits<{
	/** After Apply wrote to the draft — the tab re-reads its own Hold budget and toggle from it. */
	applied: [];
}>();

const store = useBasePriceStore();
const target = computed(() => props.target);

const {
	dates,
	weeks,
	monthKey,
	monthName,
	monthIndex,
	loadingImpressions,
	shares,
	dayGross,
	weekRange,
	step,
} = useMonthCalendar(target, month, () => seed());

const dayRows = useMonthDayRows(monthKey, dates);

// --- What the month gives every day ---

const monthFill = computed(() => props.target.monthly_fill_rates[monthIndex.value] ?? 0);

/** The month's own hand-set price, if it has one — what a day without its own falls back to. */
const monthManualCpm = computed<number | null>(() => {
	const value = Object.values(props.target.monthly_manual_cpm)[monthIndex.value];

	return value !== null && value !== undefined && value > 0 ? value : null;
});

/**
 * The month view's price bounds still apply to every day: they are the
 * target's price policy, and the resolver clamps day prices with them as it
 * does month prices. The calendar sets none of its own.
 */
const bounds = computed(() => ({ min: props.target.cpm_min, max: props.target.cpm_max }));

/**
 * Each day's budget, as the Monthly Budget Distribution calendar spread the
 * month: its locked days, this target's own pins, and the rest by impressions.
 */
const budgets = computed(() =>
	dayBudgetsFor(
		dates.value,
		shares.value,
		dayRows.effectiveDays("day_season"),
		dayRows.ownPins(),
		props.target.monthly_budget[monthIndex.value] ?? 0
	)
);

// --- Draft ---

/** Fill rates set on individual days. A day not listed follows the month. */
const fills = ref<DayMap<number>>({});
/**
 * Prices set by hand on individual days. A day not listed takes the month's
 * hand-set price, or derives from its budget when the month has none; a 0
 * says "derive", which is how a day opts out of a month's hand-set price.
 */
const cpms = ref<DayMap<number>>({});
const original = ref<{ fills: DayMap<number>; cpms: DayMap<number> }>({ fills: {}, cpms: {} });
const daysDirty = ref(false);

/**
 * Restores what was applied: the days set in the rows that decide this month
 * — the target's own, or inherited month by month like every day-level row.
 */
const seed = () => {
	fills.value = { ...dayRows.effectiveDays("day_fill") };
	cpms.value = { ...dayRows.effectiveDays("day_manual_cpm") };
	original.value = { fills: { ...fills.value }, cpms: { ...cpms.value } };
	daysDirty.value = false;
};

const manualOf = (date: string): number | null => {
	const own = cpms.value[date];
	if (own !== undefined) return own > 0 ? own : null;

	return monthManualCpm.value;
};

const priced = computed<DayMap<DayPrice>>(() =>
	Object.fromEntries(
		dates.value.map((date) => [
			date,
			priceDay(
				dayGross(date),
				budgets.value[date] ?? 0,
				fills.value[date] ?? monthFill.value,
				manualOf(date),
				bounds.value
			),
		])
	)
);

const cpmOwn = (date: string) => (cpms.value[date] ?? 0) > 0;

/**
 * Does this day set its own fill or price in this month's draft? Tinted like
 * a locked day in the Monthly Budget Distribution calendar, so what was set
 * by hand stands out from what follows the month.
 */
const overridden = (date: string) =>
	fills.value[date] !== undefined || cpms.value[date] !== undefined;
const isManual = (date: string) => manualOf(date) !== null;

const round1 = (value: number) => Math.round(value * 10) / 10;
const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Sets one fill on every day in `list` — a week edit is the same value on
 * each of its days. With Hold budget on, the price re-derives so each day
 * still earns its own budget, exactly as editing a month's fill does.
 */
const setFill = (list: string[], value: number) => {
	if (Number.isNaN(value)) return;

	const next = round1(Math.min(100, Math.max(0, value)));
	const nextFills = { ...fills.value };
	const nextCpms = { ...cpms.value };

	list.forEach((date) => {
		nextFills[date] = next;
		if (props.hold) derive(nextCpms, date);
	});

	fills.value = nextFills;
	cpms.value = nextCpms;
	daysDirty.value = true;
};

/**
 * Sets one price on every day in `list`, or clears it when empty. With Hold
 * budget on, each day's fill moves so it still earns its own budget at that
 * price, as a month's does.
 */
const setCpm = (list: string[], raw: string) => {
	const nextFills = { ...fills.value };
	const nextCpms = { ...cpms.value };

	if (raw.trim() === "") {
		list.forEach((date) => derive(nextCpms, date));
	} else {
		const value = round2(Math.max(0, Number(raw)));
		if (Number.isNaN(value)) return;

		list.forEach((date) => {
			nextCpms[date] = value;

			if (props.hold && value > 0) {
				nextFills[date] = round1(
					solveFillForCpm(priced.value[date].gross, priced.value[date].budget, value)
				);
			}
		});
	}

	fills.value = nextFills;
	cpms.value = nextCpms;
	daysDirty.value = true;
};

/** Back to budget ÷ sellable — past the month's own hand-set price too, when it has one. */
const derive = (map: DayMap<number>, date: string) => {
	if (monthManualCpm.value !== null) {
		map[date] = 0;
	} else {
		delete map[date];
	}
};

/**
 * Would solving change anything? Only a day priced by hand — its own price,
 * or the month's — can be off its budget here, so with none of those every
 * day already earns exactly its budget.
 */
const solveWouldChange = computed(() => dates.value.some((date) => manualOf(date) !== null));

/** Clears every hand-set price this month, so each day earns exactly the budget it was given. */
const solveToBudget = () => {
	const nextCpms: DayMap<number> = {};
	dates.value.forEach((date) => derive(nextCpms, date));

	cpms.value = nextCpms;
	daysDirty.value = true;
};

// --- Day state ---

/**
 * Which of a day's two figures is set by hand: neither (AUTO — it follows the
 * month's fill and price), its price (CPM), or only its fill (FILL). A price
 * set with Hold budget on solves the fill too, but the price is what was set,
 * so the day reads CPM.
 */
type DayState = "auto" | "cpm" | "fill";

const stateOf = (date: string): DayState =>
	cpmOwn(date) ? "cpm" : fills.value[date] !== undefined ? "fill" : "auto";

/** Same order the button steps through: AUTO → CPM → FILL → AUTO. */
const NEXT_STATE: Record<DayState, DayState> = { auto: "cpm", cpm: "fill", fill: "auto" };

const STATE_TITLE: Record<DayState, string> = {
	auto: "Follows the month's fill and price — click to hold today's price",
	cpm: "Price set by hand — click to hold the fill instead",
	fill: "Fill set by hand — click to follow the month again",
};

/**
 * Moves days into a state, the way the Monthly Budget Distribution calendar's
 * button does. Holding a figure takes it at what the day shows right now, so
 * the step changes nothing on screen — only what stays put when something
 * else moves. AUTO hands the day back to the month entirely.
 */
const setState = (list: string[], state: DayState) => {
	const nextFills = { ...fills.value };
	const nextCpms = { ...cpms.value };

	list.forEach((date) => {
		const day = priced.value[date];

		if (state === "auto") {
			delete nextFills[date];
			delete nextCpms[date];
		}

		if (state === "cpm") {
			nextCpms[date] = round2(day.cpm);
		}

		if (state === "fill") {
			nextFills[date] = round1(day.fill);
			derive(nextCpms, date);
		}
	});

	fills.value = nextFills;
	cpms.value = nextCpms;
	daysDirty.value = true;
};

const cycleDay = (date: string) => setState([date], NEXT_STATE[stateOf(date)]);

const weekState = (week: CalendarWeek): DayState | "mixed" => {
	const states = week.inMonth.map(stateOf);

	return states.every((state) => state === states[0]) ? states[0] : "mixed";
};

/** A mixed week takes the first step every one of its days can take. */
const cycleWeek = (week: CalendarWeek) => {
	const state = weekState(week);

	setState(week.inMonth, state === "mixed" ? "cpm" : NEXT_STATE[state]);
};

// --- Display ---

const weekDays = (week: CalendarWeek) => sumDays(week.inMonth.map((date) => priced.value[date]));
const monthTotal = computed(() => sumDays(dates.value.map((date) => priced.value[date])));

const maxCpm = computed(
	() => Math.max(...dates.value.map((date) => priced.value[date].cpm), 1) * 1.15
);

const fillBarWidth = (date: string) => `${Math.min(100, priced.value[date].fill)}%`;
const cpmBarWidth = (date: string) =>
	`${Math.min(100, (priced.value[date].cpm / maxCpm.value) * 100)}%`;


const deltaLabel = (delta: number, budget: number) =>
	isOffBudget(delta, budget)
		? `${delta > 0 ? "+" : ""}${formatNumberForScale("money", delta)}`
		: "at budget";

const deltaClass = (delta: number, budget: number) =>
	!isOffBudget(delta, budget) ? "text-success" : delta < 0 ? "text-error" : "text-info";

/** How a day is priced, in the terms its two inputs show. */
const whyText = (date: string): string => {
	const day = priced.value[date];
	const sellable = formatNumberForScale("number_no_fraction", day.sellable);
	const budget = formatNumberForScale("money", day.budget);

	if (isManual(date)) {
		return `${cpmOwn(date) ? "Set by hand" : `${monthName.value}'s hand-set price`} at ${formatNumber(day.cpm, "money")} × ${sellable} sellable ÷ 1000 = ${formatNumberForScale("money", day.revenue)}, against a budget of ${budget}.`;
	}

	return `Derived: ${budget} budget ÷ (${sellable} sellable ÷ 1000) = ${formatNumber(day.cpm, "money")}.`;
};

const offBudgetDays = computed(
	() => dates.value.filter((date) => isOffBudget(priced.value[date].delta, priced.value[date].budget)).length
);

const gaugeStats = computed(() => {
	const prices = dates.value
		.map((date) => priced.value[date])
		.filter((day) => day.sellable > 0)
		.map((day) => day.cpm);

	return [
		{ label: "Avg CPM", value: formatNumber(monthTotal.value.cpm, "money") },
		{
			label: "Min / Max day",
			value: prices.length
				? `${formatNumber(Math.min(...prices), "money")} – ${formatNumber(Math.max(...prices), "money")}`
				: "—",
		},
		{ label: "Avg fill", value: `${monthTotal.value.fill.toFixed(1)}%` },
	];
});

// --- Apply / Cancel ---

/** Hold budget and the toggle as stored on the target's fill row, or the plan's defaults without one. */
const storedHold = computed(() => {
	const fill = store.targetOverride("fill");

	return fill ? fill.hold !== "free" : store.plan?.default_lock !== "free";
});
const storedMetric = computed(() => store.targetOverride("fill")?.primary_metric ?? "cpm");

const dirty = computed(
	() =>
		daysDirty.value ||
		props.hold !== storedHold.value ||
		props.metric !== storedMetric.value
);

/**
 * Writes this month's days into the target's own rows, leaving every other
 * month alone — day fill and day CPM inherit month by month, so applying
 * December makes December the target's own and nothing else. Hold budget and
 * the toggle are saved where the month view saves them, on the fill row:
 * there is one of each for the whole configuration.
 */
const apply = () => {
	dayRows.writeMonth("day_fill", fills.value);
	dayRows.writeMonth("day_manual_cpm", cpms.value);

	if (props.hold !== storedHold.value || props.metric !== storedMetric.value) {
		const fill = store.targetOverride("fill");

		store.setTargetOverride("fill", {
			monthly_fill_rates: fill?.monthly_fill_rates ?? props.target.monthly_fill_rates.slice(),
			hold: props.hold ? "budget" : "free",
			primary_metric: props.metric,
		});
	}

	original.value = { fills: { ...fills.value }, cpms: { ...cpms.value } };
	daysDirty.value = false;
	emit("applied");
};

const cancel = () => {
	fills.value = { ...original.value.fills };
	cpms.value = { ...original.value.cpms };
	daysDirty.value = false;
};

/** Does this target set day-level fill or CPM of its own in this month? Whether the tab shows Reset to inherited. */
const ownsMonth = computed(
	() => dayRows.decidesOwn("day_fill") || dayRows.decidesOwn("day_manual_cpm")
);

/** Hands this month back to whatever is above, leaving every other month as it was. */
const resetToInherited = () => {
	dayRows.clearMonth("day_fill");
	dayRows.clearMonth("day_manual_cpm");

	seed();
};

// The tab renders the reset in its own toolbar, next to its toggles.
defineExpose({ ownsMonth, resetToInherited });
</script>

<template>
	<div>
		<div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-4">
			<div>
				<div class="text-body-1 font-weight-bold">
					Price {{ monthName }}'s
					{{ formatNumberForScale("money", target.monthly_budget[monthIndex] ?? 0) }}
					day by day
				</div>
				<div class="text-caption text-medium-emphasis d-flex align-center ga-1">
					<v-icon icon="fa-light fa-lock" size="10" />
					Each day's budget comes from Monthly Budget Distribution · days follow
					the month's {{ monthFill.toFixed(1) }}% fill
					{{
						monthManualCpm !== null
							? `and its ${formatNumber(monthManualCpm, "money")} hand-set price`
							: "and price at budget ÷ sellable"
					}}
					until they get their own
				</div>
			</div>

			<div class="d-flex align-center ga-1">
				<v-btn
					icon="fa-light fa-chevron-left"
					size="small"
					variant="text"
					:disabled="month === 1"
					aria-label="Previous month"
					@click="step(-1)"
				/>
				<v-btn
					icon="fa-light fa-chevron-right"
					size="small"
					variant="text"
					:disabled="month === 12"
					aria-label="Next month"
					@click="step(1)"
				/>
			</div>
		</div>

		<plan-revenue-gauge
			class="mb-4"
			:revenue="monthTotal.revenue"
			:budget="monthTotal.budget"
			:title="`Expected revenue · ${monthName} · ${target.name}`"
			tracks-pending
			:pending="dirty"
			:context="`against ${monthName}'s budget ${formatNumberForScale('money', monthTotal.budget)} · ${store.selectedBook?.name ?? ''}`"
			:stats="gaugeStats"
		/>

		<div class="d-flex ga-4 flex-wrap mb-2 text-caption text-medium-emphasis">
			<span class="d-flex align-center ga-1">
				<span class="legend-square" :style="{ background: FILL_COLOR }" />
				Fill rate
			</span>
			<span class="d-flex align-center ga-1">
				<span class="legend-square" :style="{ background: CPM_COLOR }" />
				CPM
			</span>
		</div>
		<div class="text-caption text-medium-emphasis mb-3">
			{{
				hold
					? "Hold budget is on: changing a fill re-derives the price, and changing a CPM moves fill so the day still earns its budget."
					: "Hold budget is off: fill and CPM move independently, so a day can earn more or less than its budget."
			}}
		</div>

		<v-progress-linear
			v-if="loadingImpressions"
			indeterminate
			color="primary"
			class="mb-2"
		/>

		<plan-month-calendar-grid
			:weeks="weeks"
			:loading="loadingImpressions"
			ghost-hint="price it there"
			:day-class="(date: string) => (overridden(date) ? 'calendar-cell--tone-warning' : '')"
			:cell-min-height="140"
		>
			<template #day="{ day }">
				<div class="d-flex align-center justify-space-between">
					<span class="text-body-2 font-weight-bold">{{ day.day }}</span>
					<button
						class="state-btn"
						:class="`state-btn--${stateOf(day.date)}`"
						:title="STATE_TITLE[stateOf(day.date)]"
						@click="cycleDay(day.date)"
					>
						{{ stateOf(day.date).toUpperCase() }}
					</button>
				</div>

				<div class="d-flex flex-column" :class="{ 'flex-column-reverse': metric === 'cpm' }">
					<div
						class="fillcpm-input-box"
						:class="{ 'fillcpm-input-box--fill': stateOf(day.date) === 'fill' }"
					>
						<input
							type="number"
							step="0.1"
							:value="priced[day.date].fill.toFixed(1)"
							:aria-label="`${day.date} fill rate`"
							@change="setFill([day.date], Number(($event.target as HTMLInputElement).value))"
						/>
						<span class="fillcpm-input-affix">%</span>
					</div>
					<div
						class="fillcpm-input-box"
						:class="{
							'fillcpm-input-box--manual': isManual(day.date),
							'fillcpm-input-box--derived': !isManual(day.date),
						}"
					>
						<span class="fillcpm-input-affix">€</span>
						<input
							type="number"
							step="0.01"
							:value="priced[day.date].cpm.toFixed(2)"
							:aria-label="`${day.date} CPM`"
							@change="setCpm([day.date], ($event.target as HTMLInputElement).value)"
						/>
					</div>
				</div>

				<v-tooltip :text="whyText(day.date)" max-width="300">
					<template v-slot:activator="{ props: tooltipProps }">
						<div v-bind="tooltipProps" class="day-bars">
							<div class="day-bar">
								<div
									class="day-bar-fill"
									:style="{
										width: fillBarWidth(day.date),
										background: FILL_COLOR,
										opacity: metric === 'fill' ? 1 : 0.5,
									}"
								/>
							</div>
							<div class="day-bar">
								<div
									class="day-bar-fill"
									:style="{
										width: cpmBarWidth(day.date),
										background: CPM_COLOR,
										opacity: metric === 'cpm' ? 1 : 0.5,
									}"
								/>
							</div>
						</div>
					</template>
				</v-tooltip>

				<div class="d-flex justify-space-between align-baseline day-footer">
					<span class="text-medium-emphasis">
						{{ formatNumberForScale("money", priced[day.date].revenue) }}
					</span>
					<span
						class="font-weight-bold"
						:class="deltaClass(priced[day.date].delta, priced[day.date].budget)"
					>
						{{ deltaLabel(priced[day.date].delta, priced[day.date].budget) }}
					</span>
				</div>
			</template>

			<template #week="{ week }">
				<div class="d-flex align-center justify-space-between">
					<span class="text-body-2 font-weight-bold">Wk {{ week.isoWeek }}</span>
					<button
						class="state-btn"
						:class="`state-btn--${weekState(week)}`"
						title="Applies to every day of this week in this month"
						@click="cycleWeek(week)"
					>
						{{ weekState(week).toUpperCase() }}
					</button>
				</div>
				<div class="text-caption text-medium-emphasis">{{ weekRange(week) }}</div>

				<div class="d-flex flex-column" :class="{ 'flex-column-reverse': metric === 'cpm' }">
					<div class="fillcpm-input-box">
						<input
							type="number"
							step="0.1"
							:value="weekDays(week).fill.toFixed(1)"
							:aria-label="`Week ${week.isoWeek} fill rate`"
							title="Sets this fill on every day of the week in this month"
							@change="setFill(week.inMonth, Number(($event.target as HTMLInputElement).value))"
						/>
						<span class="fillcpm-input-affix">%</span>
					</div>
					<div class="fillcpm-input-box">
						<span class="fillcpm-input-affix">€</span>
						<input
							type="number"
							step="0.01"
							:value="weekDays(week).cpm.toFixed(2)"
							:aria-label="`Week ${week.isoWeek} CPM`"
							title="Sets this price on every day of the week in this month"
							@change="setCpm(week.inMonth, ($event.target as HTMLInputElement).value)"
						/>
					</div>
				</div>

				<div class="text-caption text-medium-emphasis mt-2">
					<template v-if="week.inMonth.length < 7">
						{{ week.inMonth.length }} of 7 days ·
					</template>
					{{ formatNumberForScale("money", weekDays(week).revenue) }}
				</div>
				<div
					class="text-caption font-weight-bold"
					:class="deltaClass(weekDays(week).delta, weekDays(week).budget)"
				>
					{{ deltaLabel(weekDays(week).delta, weekDays(week).budget) }}
				</div>
			</template>
		</plan-month-calendar-grid>

		<div class="text-caption text-medium-emphasis mt-2">
			Greyed days belong to the neighbouring month and are priced there. A
			week's fill or CPM is set on every one of its days in this month.
		</div>

		<div v-if="!hold && offBudgetDays > 0" class="text-caption text-medium-emphasis mt-2">
			<i class="fa-light fa-circle-info mr-1" />
			{{ offBudgetDays }} {{ offBudgetDays === 1 ? "day earns" : "days earn" }}
			more or less than its budget because Hold budget is off. Solving clears the
			hand-set prices so every day earns exactly its budget again.
		</div>

		<div class="d-flex ga-2 flex-wrap my-4">
			<v-tooltip
				:text="
					solveWouldChange
						? 'Clears every hand-set price this month, so each day earns exactly the budget it was given.'
						: 'Nothing to solve: no day is priced by hand, so every day already earns exactly its budget.'
				"
				location="bottom"
			>
				<!-- On a wrapper, so the tooltip still explains a disabled button. -->
				<template v-slot:activator="{ props: tooltip }">
					<span v-bind="tooltip">
						<v-btn
							size="small"
							variant="tonal"
							:disabled="!solveWouldChange"
							@click="solveToBudget"
						>
							Solve {{ monthName }} to budget
						</v-btn>
					</span>
				</template>
			</v-tooltip>
		</div>

		<div class="d-flex justify-end ga-2">
			<v-btn variant="text" color="error" :disabled="!dirty" @click="cancel">
				Cancel
			</v-btn>
			<v-btn variant="tonal" color="primary" :disabled="!dirty" @click="apply">
				Apply {{ monthName }}
			</v-btn>
		</div>
	</div>
</template>

<style scoped lang="scss">
// The month chart's own inputs (PlanFillCpmTab.vue), repeated because both
// components scope their styles — the calendar has to read as the same editor
// one level down.
.legend-square {
	width: 9px;
	height: 9px;
	border-radius: 2px;
	display: inline-block;
}

.fillcpm-input-box {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 6px;
	height: 28px;
	border: 1.5px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

.fillcpm-input-box--manual {
	border-color: v-bind(CPM_COLOR);
}

.fillcpm-input-box--derived input {
	color: rgba(var(--v-theme-on-surface), 0.6);
	font-style: italic;
}

.fillcpm-input-box input {
	width: 100%;
	min-width: 0;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.fillcpm-input-box input::-webkit-outer-spin-button,
.fillcpm-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.fillcpm-input-box input[type="number"] {
	-moz-appearance: textfield;
}

.fillcpm-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	padding: 0 4px;
}

// --- Calendar ---

// The Monthly Budget Distribution calendar's state button, in this tab's
// colours: blue for fill, orange for price — the bars' own colours.
.state-btn {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
	background: transparent;
	color: rgba(var(--v-theme-on-surface), 0.6);
	border-radius: 6px;
	padding: 2px 6px;
	font-size: 8.5px;
	letter-spacing: 0.08em;
	cursor: pointer;
}

// Text stays in the normal ink: both colours are too light to read as text.
.state-btn--fill {
	border-color: v-bind(FILL_COLOR);
	background: v-bind(FILL_TINT);
	color: rgb(var(--v-theme-on-surface));
}

.state-btn--cpm {
	border-color: v-bind(CPM_COLOR);
	background: v-bind(CPM_TINT);
	color: rgb(var(--v-theme-on-surface));
}

.fillcpm-input-box--fill {
	border-color: v-bind(FILL_COLOR);
}

.day-bars {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 8px;
}

.day-bar {
	position: relative;
	height: 5px;
	border-radius: 3px;
	background: rgba(var(--v-theme-on-surface), 0.08);
	overflow: hidden;
}

.day-bar-fill {
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	border-radius: 3px;
	transition: width 0.1s ease;
}

.day-footer {
	margin-top: 6px;
	font-size: 11px;
	font-variant-numeric: tabular-nums;
}
</style>
